/* eslint-disable no-nested-ternary */
import { Skeleton } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';
import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { IconChevronRight } from '@tabler/icons-react';

import { reportEvent } from '~/domains/analytics/events';
import { Text } from '~/domains/design-system/Text';
import { Scroller } from '~/domains/design-system/components/Scroller/Scroller';
import { PromoCard } from '~/domains/design-system/components/PromoCard/PromoCard';
import { UnstyledButton, UnstyledButtonLink } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes'
import { PromotionSegmentType, type PromoCarouselLocation } from '~/domains/promotions/types';

import { MissionCard } from '../../domains/gamification/domains/missions/components/MissionCard/MissionCard';
import { reportMissionsTabViewed } from '../../domains/gamification/domains/missions/analyticsEvents';

import { usePromoCarousel } from './usePromoCarousel';
import { useStyles } from './PromoCarousel.styles';


type PromoCarouselProps = {
  location: PromoCarouselLocation;
  order?: number;
  contestId?: string;

  // Whether to show the header with pills and view all button.
  isHeaderVisible?: boolean;
  // Background color for the scroller. Required when `isHeaderVisible` is `true`.
  backgroundColor?: string;
};

function PromoCarousel({
  location,
  backgroundColor,
  order,
  contestId,
  isHeaderVisible,
}: PromoCarouselProps) {
  const {
    cards,
    totalCount,
    selectedPromoId,
    isLoading,
    selectedPromoTypePill,
    setPromoTypePill,
    challengesActive,
    challengesTotalCount,
    isChallengesLoading,
    handleChallengeClick,
    whalersClubPromoCard,
  } = usePromoCarousel(location, contestId);
  const { t } = useTranslation('promotions');
  const { classes, cx, theme } = useStyles();

  // Auto-scroll to selected PromoCard
  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<
    HTMLDivElement,
    HTMLDivElement
    >({ axis: 'x', duration: 400, offset: 50 });

  useEffect(() => {
    if (selectedPromoId) scrollIntoView({ alignment: 'center' });
  }, [selectedPromoId, scrollIntoView]);

  const handlePillClick = (segment: PromotionSegmentType) => {
    if (segment === PromotionSegmentType.CHALLENGES) {
      reportMissionsTabViewed({ location });
    }
    setPromoTypePill(segment);
  };

  if (
    !isLoading && !isChallengesLoading
    && (totalCount < 1 && challengesTotalCount < 1 && !whalersClubPromoCard)
  ) return;

  return (
    <div className={cx(classes.container, isHeaderVisible && 'withHeader')} style={{ order }}>
      {isHeaderVisible && (<div className={classes.pillContainer}>
        <UnstyledButton
          className={cx(
            classes.pillBase,
            selectedPromoTypePill === PromotionSegmentType.PROMOTIONS && classes.pillSelected
          )}
          disabled={totalCount < 1}
          onClick={() => handlePillClick(PromotionSegmentType.PROMOTIONS)}
        >
          <Text variant="subhead-small">{t('myPromotions.segmentControl.promotions')}</Text>
        </UnstyledButton>
        <UnstyledButton
          className={cx(
            classes.pillBase,
            selectedPromoTypePill === PromotionSegmentType.CHALLENGES && classes.pillSelected
          )}
          disabled={(!whalersClubPromoCard && challengesTotalCount < 1) || isChallengesLoading}
          onClick={() => handlePillClick(PromotionSegmentType.CHALLENGES)}
        >
          <Text variant="subhead-small">{t('myPromotions.segmentControl.challenges')}</Text>
          {challengesTotalCount > 0 && (<Text variant="subhead-x-small" className={classes.pillCount}>
            {challengesTotalCount}
          </Text>)}
        </UnstyledButton>

        <UnstyledButtonLink
          className={classes.viewAllButton}
          href={Routes.promotions()}
          onClick={() => reportEvent('Home > View More - Promotions')}
        >
          <IconChevronRight size={16} />
        </UnstyledButtonLink>
      </div>)}

      <Scroller
        ref={scrollableRef}
        backgroundColor={backgroundColor || theme.white}
      >
        {isLoading ?
          Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={64} width={300} radius="lg" />
          )
        ) : (
          selectedPromoTypePill === PromotionSegmentType.PROMOTIONS ? (
            cards.map((card) => (
              <PromoCard
                key={card.id}
                {...card}
                variant={isHeaderVisible ? "fat" : "board"}
                showBorder
                isCompact
                ref={card.id === selectedPromoId ? targetRef : undefined}
              />
            ))
            ) : (
              <>
                {whalersClubPromoCard}
                {challengesActive.map((challenge) => (
                <MissionCard
                  key={challenge.assignmentId}
                  missionAssignment={challenge}
                  onClick={() => handleChallengeClick(challenge)}
                  hasBorder
                  isCompact
                  />
                ))}
              </>
          )
        )}
      </Scroller>
    </div>
  );
}

export default PromoCarousel;
